import { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    InputBase,
    IconButton,
    Typography,
    CircularProgress,
    Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useHeaderSearch } from '../api/useUnifiedSearch';
import type { UnifiedAssetResult } from '../api/unifiedSearchTypes';
import { getAssetColor } from '../hooks/useAssetPriceOverlay';

const MAX_ASSETS = 2;

export interface SelectedAsset {
    ticker: string;
    assetType: string;
    name: string;
}

interface AssetSearchOverlayProps {
    selectedAssets: SelectedAsset[];
    onAdd: (asset: SelectedAsset) => void;
    onRemove: (ticker: string) => void;
}

export function AssetSearchOverlay({ selectedAssets, onAdd, onRemove }: AssetSearchOverlayProps) {
    const theme = useTheme();
    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const blurTimer = useRef<ReturnType<typeof setTimeout>>();

    // Unscoped: this control only draws an asset price line on the event
    // chart, it never navigates to an asset route. Overlaying is part of event
    // analysis, so an events-only user may use it.
    const { assets: rawAssets, loading } = useHeaderSearch(query, 10, {
        scoped: false,
    });

    const assets = rawAssets.filter(
        (a) => !selectedAssets.some((s) => s.ticker === a.ticker),
    );
    const hasResults = focused && assets.length > 0;
    const isFull = selectedAssets.length >= MAX_ASSETS;

    const handleSelect = useCallback(
        (a: UnifiedAssetResult) => {
            if (isFull) return;
            onAdd({
                ticker: a.ticker,
                assetType: a.asset_type || 'cryptocurrency',
                name: a.asset_name || a.ticker,
            });
            setQuery('');
            setFocused(false);
            inputRef.current?.blur();
        },
        [onAdd, isFull],
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setFocused(false);
            inputRef.current?.blur();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIdx((prev) => (prev < assets.length - 1 ? prev + 1 : 0));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIdx((prev) => (prev > 0 ? prev - 1 : assets.length - 1));
            return;
        }
        if (e.key === 'Enter' && selectedIdx >= 0 && assets[selectedIdx]) {
            e.preventDefault();
            handleSelect(assets[selectedIdx]);
        }
    };

    useEffect(() => {
        setSelectedIdx(-1);
    }, [query]);

    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {selectedAssets.map((a, i) => {
                const chipColor = getAssetColor(i, isDark);
                return (
                    <Chip
                        key={a.ticker}
                        label={a.ticker}
                        size="small"
                        onDelete={() => onRemove(a.ticker)}
                        sx={{
                            height: 22,
                            fontSize: '10px',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            bgcolor: alpha(chipColor, 0.15),
                            color: chipColor,
                            borderColor: alpha(chipColor, 0.3),
                            border: 1,
                            '& .MuiChip-deleteIcon': { fontSize: '14px', color: alpha(chipColor, 0.6) },
                        }}
                    />
                );
            })}
            {!isFull && (
                <Box sx={{ position: 'relative' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: '1px',
                            bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
                            border: 1,
                            borderColor: focused ? 'primary.main' : 'transparent',
                            borderRadius: '4px',
                            transition: 'border-color 0.15s',
                            minWidth: 120,
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <InputBase
                            inputRef={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => setFocused(true)}
                            onBlur={() => {
                                blurTimer.current = setTimeout(() => setFocused(false), 200);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="Add asset..."
                            sx={{
                                flex: 1,
                                fontSize: '11px',
                                '& input::placeholder': {
                                    color: 'text.disabled',
                                    opacity: 0.7,
                                },
                                minWidth: 190,
                            }}
                        />
                        {loading && <CircularProgress size={10} sx={{ color: 'text.disabled' }} />}
                        {query && (
                            <IconButton
                                size="small"
                                onClick={() => setQuery('')}
                                sx={{ p: 0.15 }}
                            >
                                <CloseIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                            </IconButton>
                        )}
                    </Box>

                    {hasResults && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1300,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                borderTop: 'none',
                                borderRadius: '0 0 4px 4px',
                                boxShadow: isDark
                                    ? '0 6px 16px rgba(0,0,0,0.4)'
                                    : '0 6px 16px rgba(0,0,0,0.08)',
                                maxHeight: 200,
                                overflowY: 'auto',
                                minWidth: 190,
                            }}
                        >
                            {assets.slice(0, 8).map((a, i) => (
                                <Box
                                    key={`asset-${a.ticker}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(a);
                                    }}
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        bgcolor: selectedIdx === i ? 'action.hover' : 'transparent',
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {a.ticker}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.secondary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 120,
                                            }}
                                        >
                                            {a.asset_name}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '9px',
                                            color: 'text.disabled',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {a.asset_type}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}